import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Phone, ChevronDown, ChevronRight } from 'lucide-react';
import { GuidesEnquiryModal } from '@/components/public/GuidesEnquiryModal';
import { REGION_CONFIG } from '@/data/regions';
import { CATEGORIES } from '@/data/solutions';

const SITE_NAME = 'StartEase Agent';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ease-backend/artifacts/4auzvo9b_StartEase%20Logo.png';
const CONTACT_EMAIL = 'hi@starteaseai.com';
const CONTACT_PHONE = '+1 (205) 624-5819';
const CONTACT_PHONE_TEL = '+12056245819';

const MARKET_MENU = [
  { label: 'United States', path: '/usa', market: 'us', flyoutLabel: 'States', extraSubs: [{ name: 'Browse states', path: '/usa/states' }] },
  { label: 'United Kingdom', path: '/country/uk', market: 'uk', flyoutLabel: 'Nations' },
  { label: 'United Arab Emirates', path: '/country/uae', market: 'uae', flyoutLabel: 'Free zones' },
  { label: 'Singapore', path: '/country/singapore', market: 'singapore', flyoutLabel: 'Business districts' },
  { label: 'Canada', path: '/country/canada', market: 'canada', flyoutLabel: 'Provinces' },
];

const getSubs = (m) => {
  const cfg = REGION_CONFIG[m.market];
  if (cfg?.list?.length) return cfg.list.map((r) => ({ name: r.name, path: `${cfg.basePath}/${r.slug}` }));
  return m.extraSubs || [];
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMarketsOpen, setMobileMarketsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobilePartnersOpen, setMobilePartnersOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  const isActive = (path) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));
  const isMarketsActive = location.pathname.startsWith('/usa') || location.pathname.startsWith('/country');
  const navBase = 'text-sm font-medium py-6 whitespace-nowrap border-b-2 transition-colors';
  const navCls = (active) =>
    `${navBase} ${active ? 'text-white border-emerald-500' : 'text-slate-300 border-transparent hover:text-white'}`;

  const handleLinkClick = (href) => {
    setMobileMenuOpen(false);
    setMobileMarketsOpen(false);
    setMobileSolutionsOpen(false);
    setMobilePartnersOpen(false);
    navigate(href);
    window.scrollTo(0, 0);
  };

  const handleHomeClick = () => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      window.scrollTo(0, 0);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#0b1310]/95 backdrop-blur border-b border-white/10" data-testid="site-header">
      {/* Top contact bar */}
      <div className="bg-[#070d0b] text-white py-2 border-b border-white/5">
        <div className="container-custom flex items-center justify-between text-xs">
          <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-1.5 text-slate-300 hover:text-emerald-400 transition-colors" data-testid="topbar-email">
            <Mail className="w-3.5 h-3.5" />
            <span>{CONTACT_EMAIL}</span>
          </a>
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-1.5 text-slate-300 hover:text-emerald-400 transition-colors" data-testid="topbar-phone">
            <Phone className="w-3.5 h-3.5" />
            <span>{CONTACT_PHONE}</span>
          </a>
        </div>
      </div>

      <nav className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo — mark + name */}
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="header-logo"
            onClick={() => window.scrollTo(0, 0)}
          >
            <img src={LOGO_URL} alt={SITE_NAME} className="h-8 w-8" />
            <span className="text-lg font-semibold text-white">{SITE_NAME}</span>
          </Link>

          {/* Desktop Navigation — Home · Markets ▾ · Investment Docs · Blog */}
          <div className="hidden md:flex items-center gap-6">
            <button onClick={handleHomeClick} className={navCls(isActive('/'))} data-testid="nav-home">
              Home
            </button>

            {/* Start dropdown (hover / focus-within) */}
            <div className="relative group">
              <button
                className={`${navCls(isActive('/usa'))} inline-flex items-center gap-1`}
                data-testid="nav-start"
                aria-haspopup="true"
              >
                Start
                <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              <div
                className="absolute left-0 top-full w-80 pt-2 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 focus-within:visible focus-within:opacity-100 focus-within:translate-y-0 transition-all duration-200"
                data-testid="start-dropdown"
              >
                <div className="bg-[#111c17] border border-white/10 rounded-md shadow-2xl py-1">
                  {[
                    ['LLC', 'llc', 'Form your US LLC, end to end.'],
                    ['C-Corp', 'c-corp', 'Incorporate a US C-Corporation.'],
                    ['EIN', 'ein', 'Get your federal tax ID (EIN).'],
                    ['Registered Agent', 'registered-agent', 'A registered agent in any state.'],
                    ['Mail Forwarding', 'mail-forwarding', 'A US address with mail scanning.'],
                  ].map(([name, slug, desc], i) => (
                    <button
                      key={slug}
                      onClick={() => handleLinkClick(`/usa/${slug}`)}
                      className={`block w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors group/sol ${i > 0 ? 'border-t border-white/10' : ''}`}
                      data-testid={`nav-start-${slug}`}
                    >
                      <span className="block text-sm text-slate-200 group-hover/sol:text-white transition-colors">{name}</span>
                      <span className="mt-0.5 block text-xs text-slate-400 leading-snug">{desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Markets dropdown (hover / focus-within) */}
            {false && (
            <div className="relative group">
              <button
                className={`${navCls(isMarketsActive)} inline-flex items-center gap-1`}
                data-testid="nav-markets"
                aria-haspopup="true"
              >
                Markets
                <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              <div
                className="absolute left-0 top-full w-64 pt-2 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 focus-within:visible focus-within:opacity-100 focus-within:translate-y-0 transition-all duration-200"
                data-testid="markets-dropdown"
              >
                <div className="bg-[#111c17] border border-white/10 rounded-md shadow-2xl py-1">
                  {MARKET_MENU.map((m) => {
                    const subs = getSubs(m);
                    return (
                      <div key={m.path} className="relative group/mkt">
                        <button
                          onClick={() => handleLinkClick(m.path)}
                          className="flex items-center justify-between w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                          data-testid={`nav-market-${m.path.replace(/\//g, '-')}`}
                        >
                          {m.label}
                          {subs.length > 0 && <ChevronRight className="w-4 h-4 opacity-50" />}
                        </button>
                        {subs.length > 0 && (
                          <div
                            className="absolute left-full top-0 pl-2 w-60 invisible opacity-0 group-hover/mkt:visible group-hover/mkt:opacity-100 transition-opacity duration-150"
                            data-testid={`markets-flyout-${m.market}`}
                          >
                            <div className="bg-[#111c17] border border-white/10 rounded-md shadow-2xl py-1 max-h-[70vh] overflow-y-auto">
                              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-400/80">{m.flyoutLabel}</div>
                              {subs.map((s) => (
                                <button
                                  key={s.path}
                                  onClick={() => handleLinkClick(s.path)}
                                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                                  data-testid={`nav-sub-${s.path.replace(/\//g, '-')}`}
                                >
                                  {s.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            <button
              onClick={() => handleLinkClick('/usa/payroll')}
              className={navCls(isActive('/usa/payroll'))}
              data-testid="nav-payroll"
            >
              Payroll
            </button>

            <button
              onClick={() => handleLinkClick('/usa/sales-tax')}
              className={navCls(isActive('/usa/sales-tax'))}
              data-testid="nav-sales-tax"
            >
              Sales Tax
            </button>

            <button
              onClick={() => handleLinkClick('/usa/bookkeeping')}
              className={navCls(isActive('/usa/bookkeeping'))}
              data-testid="nav-accounting"
            >
              Accounting
            </button>
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="text-slate-300 hover:text-white text-sm font-medium px-4 py-2 whitespace-nowrap transition-colors"
              data-testid="header-login-link"
            >
              Log In
            </Link>
            <a
              href={`tel:${CONTACT_PHONE_TEL}`}
              className="hidden lg:flex items-center gap-1.5 text-slate-300 hover:text-emerald-400 text-sm font-medium whitespace-nowrap transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="w-4 h-4" />
              {CONTACT_PHONE}
            </a>
            <button
              type="button"
              onClick={() => setConsultOpen(true)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
              aria-label="Request a consultation"
              title="Request a consultation"
              data-testid="header-signup-btn"
            >
              <Mail className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile Menu toggle */}
          <button
            className="md:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" strokeWidth={2.5} /> : <Menu className="w-6 h-6" strokeWidth={2.5} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="space-y-1">
              <button onClick={handleHomeClick} className="block w-full text-left px-4 py-2.5 text-slate-200 font-medium" data-testid="nav-home-mobile">
                Home
              </button>

              <button
                onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2.5 text-slate-200 font-medium"
                data-testid="nav-start-accordion-mobile"
              >
                Start
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileSolutionsOpen && (
                <div className="pl-3 border-l border-white/10 ml-4 space-y-1">
                  {[
                    ['LLC', 'llc'],
                    ['C-Corp', 'c-corp'],
                    ['EIN', 'ein'],
                    ['Registered Agent', 'registered-agent'],
                    ['Mail Forwarding', 'mail-forwarding'],
                  ].map(([name, slug]) => (
                    <button
                      key={slug}
                      onClick={() => handleLinkClick(`/usa/${slug}`)}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white"
                      data-testid={`nav-start-mobile-${slug}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}

              {/* Markets accordion */}
              {false && (
              <button
                onClick={() => setMobileMarketsOpen(!mobileMarketsOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2.5 text-slate-200 font-medium"
                data-testid="nav-markets-mobile"
              >
                Markets
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileMarketsOpen ? 'rotate-180' : ''}`} />
              </button>
              )}
              {mobileMarketsOpen && (
                <div className="pl-3 border-l border-white/10 ml-4 space-y-1">
                  {MARKET_MENU.map((m) => {
                    const subs = getSubs(m);
                    return (
                      <div key={m.path} className="pb-1">
                        <button
                          onClick={() => handleLinkClick(m.path)}
                          className="block w-full text-left px-4 py-2 text-sm text-slate-200 font-medium hover:text-white"
                          data-testid={`nav-market-mobile-${m.path.replace(/\//g, '-')}`}
                        >
                          {m.label}
                        </button>
                        {subs.length > 0 && (
                          <div className="pl-4 grid grid-cols-2 gap-x-1">
                            {subs.map((s) => (
                              <button
                                key={s.path}
                                onClick={() => handleLinkClick(s.path)}
                                className="block text-left px-2 py-1.5 text-xs text-slate-400 hover:text-emerald-400"
                                data-testid={`nav-sub-mobile-${s.path.replace(/\//g, '-')}`}
                              >
                                {s.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button onClick={() => handleLinkClick('/usa/payroll')} className="block w-full text-left px-4 py-2.5 text-slate-200 font-medium" data-testid="nav-payroll-mobile">
                Payroll
              </button>
              <button onClick={() => handleLinkClick('/usa/sales-tax')} className="block w-full text-left px-4 py-2.5 text-slate-200 font-medium" data-testid="nav-sales-tax-mobile">
                Sales Tax
              </button>
              <button onClick={() => handleLinkClick('/usa/bookkeeping')} className="block w-full text-left px-4 py-2.5 text-slate-200 font-medium" data-testid="nav-accounting-mobile">
                Accounting
              </button>

              <div className="pt-4 px-4 space-y-2">
                <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center justify-center gap-2 text-slate-200 border border-white/20 py-3 rounded mb-2" data-testid="header-phone-link-mobile">
                  <Phone className="w-4 h-4" /> {CONTACT_PHONE}
                </a>
                <Link to="/login" className="block text-center text-slate-200 border border-white/20 py-3 rounded mb-2">Log In</Link>
                <button type="button" onClick={() => { setMobileMenuOpen(false); setConsultOpen(true); }} className="block w-full text-center bg-emerald-600 text-white py-3 rounded hover:bg-emerald-500 transition-colors" data-testid="header-signup-btn-mobile">Request a consultation</button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <GuidesEnquiryModal open={consultOpen} onOpenChange={setConsultOpen} />
    </header>
  );
};

export default Header;
